import * as React from 'react';
import { InputGroup } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import {
  ConditionElement,
  Entity,
  Field,
  Filter,
  getFieldArray,
  getFieldTypeDefaultFilter,
  isFieldFilterable,
  Option,
  QueryOptions,
  Row,
} from 'gossamer-unified';
import { Dropdown, SearchCriterion } from 'gossamer-front-end';

interface SearchCriteriaBlock<T extends Row> {
  entity: Entity<T>;
  queryOptions?: QueryOptions;
  updateQueryOptions?: (newQueryOptions: QueryOptions) => void;
}

export const SearchCriteriaBlock = <T extends Row>(props: SearchCriteriaBlock<T>) => {
  if (!props.queryOptions || !props.updateQueryOptions) {
    return null;
  }

  const queryOptions = props.queryOptions;
  const updateQueryOptions = props.updateQueryOptions;
  const entityFields = props.entity.fields as Record<string, Field>;

  const newFields: Option[] = React.useMemo(
    () =>
      getFieldArray(props.entity)
        .filter(([field, _]: [Field, string]) => isFieldFilterable(field))
        .map(([field, fieldId]: [Field, string]) => {
          return {
            id: fieldId,
            label: field.label,
          };
        }),
    [props.entity],
  );

  const handleFilterChange = (index: number, newFilter: Filter) => {
    const newQueryOptions = cloneDeep(queryOptions);
    newQueryOptions.where = newQueryOptions.where || { and: [] };
    newQueryOptions.where.and = newQueryOptions.where.and || [];
    newQueryOptions.where.and[index] = newFilter;
    updateQueryOptions(newQueryOptions);
  };

  const handleAddFilter = (fieldId: string) => {
    const newQueryOptions = cloneDeep(queryOptions);
    newQueryOptions.where = newQueryOptions.where || { and: [] };
    newQueryOptions.where.and = newQueryOptions.where.and || [];
    newQueryOptions.where.and.push({
      fieldId,
      operator: getFieldTypeDefaultFilter(entityFields[fieldId].type),
      value: '',
    });
    updateQueryOptions(newQueryOptions);
  };

  const handleDeleteFilter = (index: number) => {
    const newQueryOptions = cloneDeep(queryOptions);
    newQueryOptions.where = newQueryOptions.where || { and: [] };
    newQueryOptions.where.and = newQueryOptions.where.and || [];
    newQueryOptions.where.and.splice(index, 1);
    updateQueryOptions(newQueryOptions);
  };
  const filters = queryOptions.where?.and || [];
  return (
    <>
      {filters.map((conditionElement: ConditionElement, index) => {
        const filter = conditionElement as Filter;
        if (!filter.fieldId || filter.display === 'hidden') {
          return null;
        }
        return (
          <SearchCriterion
            display={filter.display || 'full-edit'}
            key={filter.uniqueId || filter.fieldId + index}
            entity={props.entity}
            filter={conditionElement as Filter}
            handleChange={handleFilterChange.bind(null, index)}
            handleDelete={handleDeleteFilter.bind(null, index)}
          />
        );
      })}
      <InputGroup size="sm" className="mb10px addSearchFilter">
        <InputGroup.Text>Add a search filter</InputGroup.Text>
        <Dropdown
          clearOnSelection={true}
          mandatory={false}
          onItemSelected={handleAddFilter}
          options={newFields}
          value=""
        />
      </InputGroup>
    </>
  );
};
